import React, { useState, useRef, useEffect } from "react";
import Button from '@mui/material/Button';
import paper from "paper";
import NoteMetaData from "./metaData";
import FretboardSettings from "./FretboardSettings";
import TuningControl from "./TuningControl";
import { Box, Grid, Typography } from '@mui/material';
import FretboardMenu from "./FretboardMenu";
import OldFretboardModal from "./OldFretboardModal";
import styles from "../styles";
import CodeInput from "./CodeInput";
import DescriptionInput from "./DescriptionInput";
import update from 'immutability-helper';
import Paywall from "./Paywall";
import FretboardDiagramTour from "./FretboardDiagramTour";
import CodeIcon from '@mui/icons-material/Code';
import GIF from 'gif.js';

/**
 * Represents a Fretboard component.
 *
 * @param {Object} props - The props for the Fretboard component.
 * @returns {JSX.Element} The Fretboard component.
 */
function Fretboard(props) {

    //this is the state for the UI aspect of fretboard
    const [selectedFrets, _setSelectedFrets] = useState({}); //this is the currently written fretboard.  this array is used to draw the fretboard
    const [prevSelectedFrets, _setPrevSelectedFrets] = useState({});
    const [noteBorderColor, _setNoteBorderColor] = useState("#1A578E");
    const [noteFillColor, _setNoteFillColor] = useState("#1A578E");
    const [noteTextColor, _setNoteTextColor] = useState("#FFFFFF");
    const [noteText, _setNoteText] = useState("");
    const [noteNumber, _setNoteNumber] = useState(1); //this is the current number to be shown in 
    const [showNumbers, _setShowNumbers] = useState(false);
    const [autoIncrement, _setAutoIncrement] = useState(false);
    const [noteTiming, _setNoteTiming] = useState(1);
    const [stringCount, _setStringCount] = useState(6);
    const [fretboardTitle, _setFretboardTitle] = useState("");
    const [fretboardColor, setFretboardColor] = useState("#FFFFFF");
    const [tuning, _setTuning] = useState(["e", "B", "G", "D", "A", "E"]);
    const [showFretNote, _setShowFretNote] = useState(false);
    const [showTuningOnNut, _setShowTuningOnNut] = useState(true);
    const [error, setError] = useState("");
    const [dragStartFret, setDragStartFret] = useState(-1);
    const [dragStartString, setDragStartString] = useState(0);
    const [string_spacing, setStringSpacing] = useState(20);
    const [light_radius, setLightRadius] = useState((string_spacing / 2) - 1);
    const [xcsrf, setXcsrf] = useState("");
    const [Drawings, _setDrawings] = useState({
        current: 0,
        collection: []
    });
    const [Fretboard, _setFretboard] = useState({});
    const [isOld, setIsOld] = useState(false); // previous version of fretboard modal
    const [showCode, setShowCode] = useState(false);
    const [displayCodeBlock, setDisplayCodeBlock] = useState(true);
    const [updateCode, setUpdateCode] = useState(false);
    const [showFretboardModal, setShowFretboardModal] = useState(false);
    const [animateFretboard, _setAnimateFretboard] = useState(false);
    const [centerMarkers, setCenterMarkers] = useState(true);
    const [showPaywall, setShowPaywall] = useState(false);
    const [isPremium, _setIsPremium] = useState(false);
    const [disableShowCode, setDisableShowCode] = useState(false); //
    /*
    * below is a copy of the value in the description textarea.  
    * We use it to be able to update the description when a diagram is selected and allow component refresh
    * The description of the current diagram needs to be saved to the description textarea like the fretboard code
    * so that it can be saved to the database
    * */
    const [fretboardDescription, _setFretboardDescription] = useState("");

    // these are the refs for the above states
    // they are used to get the current value of the state within a MouseEvent
    const stringCountRef = useRef(stringCount);
    const fretboardTitleRef = useRef(fretboardTitle);
    const NoteBorderColorRef = useRef(noteBorderColor);
    const noteFillColorRef = useRef(noteFillColor);
    const noteTextColorRef = useRef(noteTextColor);
    const noteTextRef = useRef(noteText);
    const noteTimingRef = useRef(noteTiming);
    const selectedFretsRef = useRef(selectedFrets);
    const tuningRef = useRef(tuning);
    const showFretNoteRef = useRef(showFretNote);
    const showTuningOnNutRef = useRef(showTuningOnNut);
    const DrawingsRef = useRef(Drawings);
    const FretboardRef = useRef(Fretboard);
    const animateFretboardRef = useRef(animateFretboard);
    const fretboardDescriptionRef = useRef(fretboardDescription);
    const prevSelectedFretsRef = useRef(prevSelectedFrets); //retain the copy to stop a cancelled edit
    const noteNumberRef = useRef(noteNumber);
    const autoIncrementRef = useRef(autoIncrement);
    const showNumbersRef = useRef(showNumbers);
    const isPremiumRef = useRef(isPremium);

    /**
     * Updates the selected frets and sets the new selected frets.
     * @param {Function} updateFunction - The function used to update the selected frets.
     * @returns {void}
     */
    const setSelectedFrets = (updateFunction) => {
        const newSelectedFrets = updateFunction(selectedFretsRef.current);
        selectedFretsRef.current = newSelectedFrets;
        _setSelectedFrets(newSelectedFrets);
    };

    const replaceSelectedFrets = (drawing) => {
        prevSelectedFretsRef.current = selectedFretsRef.current;
        _setPrevSelectedFrets(selectedFretsRef.current);
        selectedFretsRef.current = drawing.data;
        _setSelectedFrets(drawing.data);
        getFretboardText(drawing.data);
        if (hasSelectedFrets()) {
            setDisplayCodeBlock(false);
            setDisableShowCode(true);
        }
        //update the description
        setFretboardDescription(drawing.description);
        redrawFretboard();

    };

    const cancelReplaceSelectedFrets = () => {
        const newSelectedFrets = Object.keys(prevSelectedFretsRef.current).length > 0 ? prevSelectedFretsRef.current : selectedFretsRef.current;
        selectedFretsRef.current = newSelectedFrets;
        _setSelectedFrets(newSelectedFrets);
        getFretboardText(newSelectedFrets);
        redrawFretboard();
    };

    const setStringCount = (data) => { stringCountRef.current = data; _setStringCount(data); };
    const setTitle = (data) => { fretboardTitleRef.current = data; _setFretboardTitle(data); };
    const setNoteBorderColor = (data) => { NoteBorderColorRef.current = data; _setNoteBorderColor(data); };
    const setNoteFillColor = (data) => { noteFillColorRef.current = data; _setNoteFillColor(data); };
    const setNoteTextColor = (data) => { noteTextColorRef.current = data; _setNoteTextColor(data); };
    const setNoteText = (data) => { noteTextRef.current = data; _setNoteText(data); };
    const setNoteTiming = (data) => { noteTimingRef.current = data; _setNoteTiming(data); };
    const setTuning = (data) => { tuningRef.current = data; _setTuning(data); };
    const setShowFretNote = (data) => { showFretNoteRef.current = data; _setShowFretNote(data); };
    const setShowTuningOnNut = (data) => { showTuningOnNutRef.current = data; _setShowTuningOnNut(data); };
    const setDrawings = (data) => { DrawingsRef.current = data; _setDrawings(data); };
    const setFretboard = (data) => {
        FretboardRef.current = data;
        _setFretboard(data);
    };
    const setAnimateFretboard = (data) => { animateFretboardRef.current = data; _setAnimateFretboard(data); };
    const setFretboardDescription = (data) => { fretboardDescriptionRef.current = data; _setFretboardDescription(data); };
    const setNoteNumber = (data) => { noteNumberRef.current = data; _setNoteNumber(data); };
    const setAutoIncrement = (data) => { autoIncrementRef.current = data; _setAutoIncrement(data); };
    const setShowNumbers = (data) => { showNumbersRef.current = data; _setShowNumbers(data); };
    const setIsPremium = (data) => {
        isPremiumRef.current = data;
        _setIsPremium(data);
    };

    //the image of the fretboard
    const canvasRef = useRef(null);
    let divRef = null;
    let diagramsDivRef = null;
    let descriptionDivRef = null;
    let fretboardSettingsDivRef = null;
    //the currently selected fretboard
    if (typeof window !== 'undefined') {
        divRef = document.getElementById(props.textArea) || document.createElement('textarea');
        //the textArea for the collection of fretboards
        diagramsDivRef = document.getElementById(props.diagramsArea);
        descriptionDivRef = document.getElementById(props.descriptionArea);
        //the textArea for the fretboard settings
        fretboardSettingsDivRef = document.getElementById(props.fretboardSettingsArea);
        if (divRef) {
            //the viewer could be empty because no fretboard code has been written yet
            divRef.addEventListener("input", handleTextChange);
        }

    }

    if (diagramsDivRef && diagramsDivRef.value === "") {
        diagramsDivRef.value = '{}';
    }


    if (fretboardSettingsDivRef && fretboardSettingsDivRef.value === "") {
        //chech if fretboardSettingsDivRef is not an empty obeject {}
        fretboardSettingsDivRef.value = '{"start_fret":1,"end_fret":23,"color":"#bbbdb5","background_color":"#FFFFFF","marker_color":"#aaa","marker_radius":4,"nut_color":"#aaaaaa","nut_font_color":"#000","nut_width":10,"width":750,"x":10,"y":20,"font_color":"black","font_face":"Arial","font_size":12,"total_frets":23,"showCode":false,"animate":false,"centerMarkers":true,"orientation":"landscape"}'; // the settings define the fretboard
        if (!props.isAnon) { //this covers the case of an anonymous user who is creating a new fretboard, they are not saving anything or loading anything
            setIsOld(true);
        }
    }

    //some items that were being set on the fly
    let fretXs = [];
    let lights = [];

    //some svg padding
    const svgPaddingX = 4;
    const svgPaddingY = 14;

    const getNoteMetaData = (string, fret) => {
        let text_string = "";
        if (showFretNoteRef.current) {

            text_string = getNoteFromFret(string, fret, FretboardRef.current.orientation === "landscape" ? false : true)
        } else if (showNumbersRef.current) {
            text_string = noteNumberRef.current;
        } else if (noteTextRef.current) {
            text_string = noteTextRef.current;
        }
        return {
            "color": NoteBorderColorRef.current,
            "fill_color": noteFillColorRef.current,
            "text_color": noteTextColorRef.current,
            "text_string": text_string
        };
    };

    const getSessionToken = async () => {
        try {
            const response = await fetch('/session/token');
            const data = await response.text();
            setXcsrf(data);
        } catch (error) {
            console.error(error);
        }
    };

    const handleUpgrade = () => {
        setIsPremium(true);
        setShowPaywall(false);
    }

    useEffect(() => {
        setAnimateFretboard(FretboardRef.current.animate);
    }, [Fretboard]);

    useEffect(() => {
        if (animateFretboard) {
            setAnimateFretboard(animateFretboard);
        }
    }, [animateFretboard]);

    useEffect(() => {
        if (updateCode) {
            redrawFretboard();
            setUpdateCode(false);
        }
    }, [updateCode]);

    useEffect(() => {
        if (showFretboardModal) {
            setShowFretboardModal(showFretboardModal);
        }
    }, [showFretboardModal]);

    //write a useEffect that sets the css on divref to a class called "showCode" when showcode is true
    useEffect(() => {
        if (showCode) {
            setShowCode(true);
        } else {
            setShowCode(false);
        }
    }, [showCode]);

    useEffect(() => {
        const fetchData = async () => {
            await getSessionToken();
        };
        setIsPremium(props.premium);
        fetchData();
        paper.setup(canvasRef.current);
        if (!props.viewer) {
            canvasRef.current.addEventListener("mousedown", handleMouseDown);
            canvasRef.current.addEventListener("mouseup", handleMouseUp);
        }
        parse();
        draw();
        lightsCameraAction();
        try {
            if (fretboardSettingsDivRef.value === "") {
                setFretboard(JSON.parse('{"start_fret":1,"end_fret":23,"color":"#bbbdb5","background_color":"#FFFFFF","marker_color":"#aaa","marker_radius":4,"nut_color":"#aaaaaa","nut_font_color":"#000","nut_width":10,"width":750,"x":10,"y":20,"font_color":"black","font_face":"Arial","font_size":12,"total_frets":23,"showCode":false,"animate":true,"centerMarkers":true,"orientation":"landscape"}')); // the settings define the fretboard
            } else {
                const fretboardSettings = JSON.parse(fretboardSettingsDivRef.value);
                if (fretboardSettings.orientation === undefined) {
                    fretboardSettings.orientation = "landscape";
                }
                setFretboard(fretboardSettings); // the settings define the fretboard
                if (fretboardSettings.savedFrets) {
                    addSavedSelectedFrets(fretboardSettings.savedFrets);
                }
            }
        } catch (error) {
            console.log("Error parsing fretboard settings JSON:", error);
            // Handle the error here (e.g., set a default value for the fretboard)
        }

        const drawings = JSON.parse(diagramsDivRef.value);

        setDrawings(drawings); // these are all the diagrams

        if (descriptionDivRef && descriptionDivRef.value) {
            setFretboardDescription(descriptionDivRef.value);
        }
        if (hasSelectedFrets()) {
            setDisplayCodeBlock(false);
            setDisableShowCode(true);
        }
        redrawFretboard();
        return () => {
            canvasRef.current.removeEventListener("mousedown", handleMouseDown);
            canvasRef.current.removeEventListener("mouseup", handleMouseUp);
        }
    }, []);

    useEffect(() => {
        canvasRef.current.width = parseInt(FretboardRef.current.width) + svgPaddingX;
        paper.setup(canvasRef.current);
        setCanvasSize();
        if (!props.viewer) {
            canvasRef.current.addEventListener("mousedown", handleMouseDown);
            canvasRef.current.addEventListener("mouseup", handleMouseUp);
        }
        if (divRef) {
            parse();
            draw();
        }
        lightsCameraAction();
        setAnimateFretboard(FretboardRef.current.animate);
        setCenterMarkers(FretboardRef.current.centerMarkers);
        setDrawings(JSON.parse(diagramsDivRef.value));
        redrawFretboard();
        fretboardSettingsDivRef.value = JSON.stringify(FretboardRef.current);
        return () => {
            canvasRef.current.removeEventListener("mousedown", handleMouseDown);
            canvasRef.current.removeEventListener("mouseup", handleMouseUp);
        }
    }, [fretboardTitle, stringCount, Fretboard, tuning, showTuningOnNut]);
    const searchSelectedFret = (stringIndex, fretIndex) => {
        if (selectedFretsRef.current[stringIndex]) {
            return selectedFretsRef.current[stringIndex].some((selectedFret) => selectedFret.fret === fretIndex);
        } else {
            return false;
        }
    };

    useEffect(() => {
        if (descriptionDivRef) {
            descriptionDivRef.value = fretboardDescriptionRef.current;
        }
        redrawFretboard();
    }, [fretboardDescription]);


    const handleAnimateToggle = () => {
        setAnimateFretboard(!animateFretboard);
        const newFretboard = update(Fretboard, {
            animate: { $set: !animateFretboard },
        });
        setFretboard(newFretboard);
    };

    const addSelectedFret = async (stringIndex, fretIndex, metadata) => {
        await setSelectedFrets((prevSelectedFrets) => {
            const selectedFrets = { ...prevSelectedFrets };
            if (!selectedFrets[stringIndex]) {
                selectedFrets[stringIndex] = [];
            }
            const existingFretIndex = selectedFrets[stringIndex].findIndex((selectedFret) => selectedFret.fret === fretIndex);
            if (existingFretIndex !== -1) {
                selectedFrets[stringIndex][existingFretIndex] = { fret: fretIndex, metadata };
            } else {
                selectedFrets[stringIndex].push({ fret: fretIndex, metadata });
            }
            getFretboardText(selectedFrets);
            return selectedFrets;
        });
    };

    //add a function like addSelectedFret that inserts an array of saved selectedFrets
    const addSavedSelectedFrets = async (savedSelectedFrets) => {
        await setSelectedFrets((prevSelectedFrets) => {
            const selectedFrets = { ...prevSelectedFrets };
            Object.entries(savedSelectedFrets).forEach(([stringIndex, savedFrets]) => {
                if (!selectedFrets[stringIndex]) {
                    selectedFrets[stringIndex] = [];
                }
                savedFrets.forEach(({ fret: fretIndex, metadata }) => {
                    const existingFretIndex = selectedFrets[stringIndex].findIndex((selectedFret) => selectedFret.fret === fretIndex);
                    if (existingFretIndex !== -1) {
                        selectedFrets[stringIndex][existingFretIndex] = { fret: fretIndex, metadata };
                    }
                    else {
                        selectedFrets[stringIndex].push({ fret: fretIndex, metadata });
                    }
                });
            });
            getFretboardText(selectedFrets);
            return selectedFrets;
        });
    };

    const removeSelectedFret = async (stringIndex, fretIndex) => {
        await setSelectedFrets((prevSelectedFrets) => {
            const selectedFrets = { ...prevSelectedFrets };
            if (selectedFrets[stringIndex]) {
                selectedFrets[stringIndex] = selectedFrets[stringIndex].filter((selectedFret) => selectedFret.fret !== fretIndex);
            }
            getFretboardText(selectedFrets);
            return selectedFrets;
        });
    };

    //a function to remove all selected frets
    const removeAllSelectedFrets = async () => {
        await setSelectedFrets((prevSelectedFrets) => {
            const selectedFrets = { ...prevSelectedFrets };
            for (const key in selectedFrets) {
                selectedFrets[key] = [];
            }
            getFretboardText(selectedFrets);
            return selectedFrets;
        });
        setFretboardDescription("");
        setNoteNumber(1);
        redrawFretboard();
    };

    const getFretboardText = (FretboardData) => {
        let fretboardText = '';
        Object.entries(FretboardData).forEach(([stringIndex, FretboardData]) => {
            FretboardData.forEach(({ fret: fretIndex, metadata: metaData }) => { //color: fillColor, text_string: text, fill_color: color, text_color: textColor
                if (fretboardText !== "") {
                    fretboardText += "\n";
                }
                fretboardText += `show frets=${fretIndex} strings=${stringIndex} color=${metaData.color} fill-color=${metaData.fill_color} text=${metaData.text_string} text-color=${metaData.text_color}`;
            });
        });
        divRef.value = fretboardText;
        return fretboardText.replace(/^\n/, '');
    };

    const updateDrawings = (newCollection) => {
        const newDrawings = { ...Drawings, collection: newCollection };
        setDrawings(newDrawings);
        diagramsDivRef.value = JSON.stringify(newDrawings);
        //if selected frets is empty, showCode
        if (hasSelectedFrets()) {
            setDisplayCodeBlock(false);
        } else {
            setDisplayCodeBlock(true);
        }
    };

    const updateDrawing = (index) => {
        //update the drawing specified by index with the new drawing specified by selectedFrets
        //and then call updateDrawings and jsonify the data for the diagrams div
        //ToDo: make sure the description is updated when a diagram is selected
        const newCollection = update(DrawingsRef.current.collection, {
            $splice: [[index, 1, { data: selectedFrets, timing: noteTimingRef.current, svg: paper.project.exportSVG({ asString: true }), description: descriptionDivRef.value }]],
        });
        updateDrawings(newCollection);
    };

    /**
     * Handles the download of the fretboard image as a GIF file.
     * @returns {void}
     */
    const handleDownloadImage = () => {
        const renderCanvas = canvasRef.current;
        //const ctx = renderCanvas.getContext('2d', { alpha: true });
        //ctx.fillStyle = '#000000';
        //ctx.fillRect(0, 0, renderCanvas.width, renderCanvas.height);


        const gif = new GIF({
            workers: 2,
            quality: 10,
            width: renderCanvas.width,
            height: renderCanvas.height,
            workerScript: "/modules/contrib/gtc/js/gtc-fretboard/node_modules/gif.js/dist/gif.worker.js",
        });

        if (FretboardRef.current.background_color === "transparent") {
            gif.setOption("transparent", FretboardRef.current.background_color);
        }

        gif.addFrame(renderCanvas.getContext('2d', { alpha: true }));
        gif.on('finished', function (blob) {
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = "guitartabcreator_fretboard.gif";
            a.click();
            URL.revokeObjectURL(url);
        });

        gif.render();
    };

    // user is clicking on the fretboard
    function handleMouseDown(event) {
        if (isOld || showCode) {
            return;
        }
        if (event.button === 2) {
            // Right-click was pressed
            return;
        }
        event.preventDefault();
        const fretIndex = calculateFretPosition(event);
        const stringIndex = calculateStringPosition(event);
        setDragStartFret(fretIndex);
        setDragStartString(stringIndex);
    }

    //helper function to fill in the integers between two numbers
    function fillIntegers(start, end) {
        const result = [];
        for (let i = start; i <= end; i++) {
            result.push(i);
        }
        return result.join(',');
    }

    //user is releasing the mouse it could be a click or a drag
    async function handleMouseUp(event) {
        if (isOld || showCode) {
            return;
        }
        if (event.button === 2) {
            // Right-click was pressed
            return;
        }
        event.preventDefault();
        let fretIndex = calculateFretPosition(event);
        const stringIndex = calculateStringPosition(event);
        if (stringIndex === -1) {
            return;
        }

        if (dragStartFret !== -1 && dragStartFret !== fretIndex) {
            //it's a drag
            const startFret = Math.min(dragStartFret, fretIndex);
            const endFret = Math.max(dragStartFret, fretIndex);
            fretIndex = fillIntegers(startFret, endFret);
        }
        if (searchSelectedFret(stringIndex, fretIndex)) {
            await removeSelectedFret(stringIndex, fretIndex);
        } else {

            const metadata = getNoteMetaData(stringIndex, fretIndex);
            await addSelectedFret(stringIndex, fretIndex, metadata);
            incrementNoteNumber(true);
        }
        setDragStartFret(-1);
        setDragStartString(0);

        //if selected frets is empty, showCode
        if (hasSelectedFrets()) {
            setDisplayCodeBlock(false);
            setDisableShowCode(true);
        } else {
            setDisplayCodeBlock(true);
            setDisableShowCode(false);
        }
        //we need to modify FretboardRef.current to add the data from selectedFrets if drawingsref collection is empty

        const newFretboard = update(FretboardRef.current, {
            savedFrets: { $set: selectedFretsRef.current },
        });
        fretboardSettingsDivRef.value = JSON.stringify(newFretboard);
        setFretboard(newFretboard);

        redrawFretboard();
    }

    //write a function that increments or decrements the note number
    function incrementNoteNumber(increment) {
        if (increment && autoIncrementRef.current && showNumbersRef.current) {
            setNoteNumber(noteNumberRef.current + 1);
        }
    }


    //write a function that checks if any key in the selectedFrets object is not empty, there can be more than one key
    function hasSelectedFrets() {
        //iterate through each key and check
        let retVal = false;
        for (const key in selectedFretsRef.current) {
            if (selectedFretsRef.current[key].length > 0) {
                retVal = true;
            }
        }
        //if there is at least one diagram, return true
        if (DrawingsRef.current.collection && DrawingsRef.current.collection.length > 0) {
            retVal = true;
        }
        return retVal;
    }

    function getNoteFromFret(string, fret, reverse = false) {
        const thisTuning = [...tuningRef.current];
        if (reverse) {
            thisTuning.reverse();

        }
        const note = thisTuning[string - 1];
        const notes = ["A", "A#", "B", "C", "C#", "D", "D#", "E", "F", "F#", "G", "G#"];
        const noteIndex = notes.indexOf(note.toUpperCase());
        const notesStartingWithNote = notes.slice(noteIndex).concat(notes.slice(0, noteIndex));
        const noteCount = notesStartingWithNote.length;
        const noteIndexWithFret = (fret % noteCount);
        return notesStartingWithNote[noteIndexWithFret];
    }

    //end user interaction functions

    function addBackground() {
        // Get the Paper.js project
        const project = paper.project;

        // Create a new rectangle that fills the entire canvas with the desired background color


        const background = new paper.Path.Rectangle({
            point: [0, 0],
            size: [project.view.size.width, project.view.size.height],
            fillColor: FretboardRef.current.background_color = "transparency" ? FretboardRef.current.background_color : "#FFFFFF",
            strokeColor: "#bbbdb5", // This will create a black border
            strokeWidth: 2
        });

        //Create a new rectangle that is smaller than the previous rectangle and is placed between the first and last string
        const backgroundHeight = getStringPos(stringCountRef.current) - getStringPos(1);
        const xCoord = FretboardRef.current.orientation === "landscape" ? FretboardRef.current.x : FretboardRef.current.y;
        const yCoord = FretboardRef.current.orientation === "landscape" ? FretboardRef.current.y : FretboardRef.current.x;
        const fretboard = FretboardRef.current.orientation === "landscape" ? new paper.Path.Rectangle({
            point: [xCoord, yCoord],
            size: [FretboardRef.current.width - 15, backgroundHeight], //
            fillColor: FretboardRef.current.background_color,
        }) : new paper.Path.Rectangle({
            point: [xCoord, yCoord + 10],
            size: [backgroundHeight, project.view.size.height - 20], //
            fillColor: FretboardRef.current.background_color,
        });

        // // Add the rectangle on top of the previous rectangle

        const opacityVal = FretboardRef.current.background_color === "transparent" ? 1.0 : 0.0;
        //background.opacity = opacityVal;
        //fretboard.opacity = opacityVal;
        background.sendToBack();
        fretboard.sendToBack();
    }

    function setCanvasSize() {
        //we should try to use the viewsize withs and heights for drawString, drawFret, drawNut, drawMarkers, showDescription, drawWatermark
        if (FretboardRef.current.orientation === "portrait") {
            paper.project.view.viewSize.width = parseInt(stringCountRef.current * string_spacing + string_spacing / 2) + svgPaddingY;
            paper.project.view.viewSize.height = parseInt(FretboardRef.current.width) + svgPaddingX;
        } else if (FretboardRef.current.orientation === "landscape") {
            paper.project.view.viewSize.width = parseInt(FretboardRef.current.width) + svgPaddingX;
            paper.project.view.viewSize.height = parseInt(stringCountRef.current * string_spacing + string_spacing / 2) + svgPaddingY;
        }
    }

    function redrawFretboard() {
        lights = [];
        if (paper.project) {
            paper.project.clear(); // clear the paper.js project
        }
        paper.project.activeLayer.removeChildren();
        // set the paper.js project width to Fretboard.width
        setCanvasSize();
        //reset();
        calculateFretPositions();
        if (divRef) {
            parse();
            draw();
        }
        addBackground();
        //if there are any frets, disable the show code button

        lightsCameraAction();
        paper.view.draw();
    }

    function handleTextChange(event) {
        event.preventDefault();
    }

    /**
     * Handles the click event of the save button. This will update state and save as JSON to a text field in drupal form
     * @param {Event} event - The click event.
     * @returns {void}
     */
    function handleSaveButtonClick(event) {
        event.preventDefault();
        const targetId = event.target.id; // Get the id of the target element
        //display the premium modal when trying to add more than 5 diagrams
        if (!isPremium && DrawingsRef.current.collection && DrawingsRef.current.collection.length >= 5) {
            setShowPaywall(true);
            return;
        }
        let position = 0;
        let newCollection = [];
        if (DrawingsRef.current.collection !== undefined) {
            position = DrawingsRef.current.collection.length;
            newCollection = DrawingsRef.current.collection;
        } else {
            newCollection = [];
        }
        setDrawings({
            current: position,
            collection: [
                ...newCollection,
                { data: selectedFrets, timing: noteTimingRef.current, svg: paper.project.exportSVG({ asString: true }), description: descriptionDivRef.value },
            ],
        });

        //update diagrams
        diagramsDivRef.value = JSON.stringify(DrawingsRef.current);
        if (targetId === "old-menu-handle-add-diagram") {
            divRef.value = "";
        }
        redrawFretboard();
    }

    const handleShowCode = (show) => {
        setShowCode(show);
        const newFretboard = update(Fretboard, {
            showCode: { $set: show },
        });
        setFretboard(newFretboard);
    }

    /**
     * Calculates the fret position based on the mouse event.
     *
     * @param {MouseEvent} event - The mouse event object.
     * @returns {number} - The calculated fret position.
     */

    function calculateFretPosition(event) {
        const canvasRect = canvasRef.current.getBoundingClientRect();
        const mouseX = FretboardRef.current.orientation === "landscape" ? event.offsetX + canvasRect.left + svgPaddingX : event.offsetY + svgPaddingX;
        const fretPositions = calculateFretPositions();
        let fretIndex = -1;
        const offSet = calculateOffset(0);
        for (let i = 0; i < fretPositions.length - 1; i++) {
            let leftside = Math.round(fretPositions[i] + offSet);
            if (leftside < calculateOffset(i)) {
                leftside = calculateOffset(i);
            }
            const rightside = Math.round(fretPositions[i + 1] + offSet);
            if (mouseX > leftside && mouseX <= rightside) {
                fretIndex = i;
                break;
            }
        }
        return fretIndex + FretboardRef.current.start_fret;
    }

    /**
     * Calculates the offset based on the fret index and the orientation of the fretboard.
     *
     * @param {number} fretIndex - The index of the fret.
     * @returns {number} The calculated offset.
     */
    function calculateOffset(fretIndex) {
        return FretboardRef.current.orientation === "landscape" ? FretboardRef.current.x + svgPaddingX : FretboardRef.current.x + svgPaddingX;
    }


    function calculateStringPosition(event) {
        const canvasRect = canvasRef.current.getBoundingClientRect();
        const mouseY = FretboardRef.current.orientation === "landscape" ? event.clientY - canvasRect.top : event.clientX - canvasRect.left;
        let stringIndex = -1;
        for (let i = 1; i <= stringCountRef.current; i++) {
            const stringY = getStringPos(i, FretboardRef.current.y);
            if (mouseY >= stringY - string_spacing / 2 && mouseY <= stringY + string_spacing / 2) {
                stringIndex = i;
                break;
            }
        }
        return stringCountRef.current - (stringCountRef.current - stringIndex);
    }

    /**
     * Calculates the positions of the frets on the fretboard.
     * @returns {number[]} An array of fret positions.
     */
    function calculateFretPositions() {
        //if !landscape, then width is a height and x is y
        var i, k, num, ref, transform, width, x;
        width = FretboardRef.current.orientation === "landscape" ? FretboardRef.current.width - 15 : paper.project.view.viewSize.height - 30;  //make the 30 a constant
        //width = FretboardRef.current.orientation === "landscape" ? FretboardRef.current.width - FretboardRef.current.nut_width : paper.project.view.viewSize.height - FretboardRef.current.nut_width - 30;  //make the 30 a constant
        let bridge_to_fret = [width];
        fretXs = [0];
        k = 1.05946;
        for (num = i = 1, ref = FretboardRef.current.total_frets; (1 <= ref ? i <= ref : i >= ref); num = 1 <= ref ? ++i : --i) {
            bridge_to_fret[num] = width / Math.pow(k, num);
            fretXs[num] = width - bridge_to_fret[num];
        }
        // We don't need the entire scale (nut to bridge), so transform
        // the X positions.
        transform = (x) => {
            return Math.ceil((x / fretXs[FretboardRef.current.total_frets]) * width);
        };
        return fretXs = (function () {
            var j, len, ref1, results;
            ref1 = fretXs;
            results = [];
            for (j = 0, len = ref1.length; j < len; j++) {
                x = ref1[j];
                results.push(transform(x));
            }
            return results;
        })();
    }

    /**
     * Draws the fretboard based on the current state of the FretboardRef.
     */
    function draw() {
        if (Object.keys(FretboardRef.current).length === 0) {
            return;
        }
        var i, j, num, ref, ref1, ref2;
        for (num = i = 1, ref = stringCountRef.current; (1 <= ref ? i <= ref : i >= ref); num = 1 <= ref ? ++i : --i) {
            drawString(num);
        }
        for (num = j = ref1 = FretboardRef.current.start_fret, ref2 = FretboardRef.current.end_fret; (ref1 <= ref2 ? j <= ref2 : j >= ref2); num = ref1 <= ref2 ? ++j : --j) {
            drawFret(num);
        }
        if (FretboardRef.current.start_fret === 1) {
            drawNut();
        } else {
            showStartFret();
        }
        drawMarkers();
        showDescription();
        drawWatermark();
        return paper.view.draw();
    }

    /**
     * Draws a string on the fretboard.
     *
     * @param {number} num - The number of the string.
     * @returns {void}
     */
    function drawString(num) {
        const path = new paper.Path();
        path.strokeColor = FretboardRef.current.color;
        const y = FretboardRef.current.orientation === "landscape" ? getStringPos(num, FretboardRef.current.y) : calculateOffset(0);
        const x = FretboardRef.current.orientation === "landscape" ? FretboardRef.current.x : getStringPos(num, FretboardRef.current.y);
        const start = new paper.Point(x, y);
        const end = FretboardRef.current.orientation === "landscape" ? FretboardRef.current.width - 15 : paper.project.view.viewSize.height - 30;
        path.moveTo(start);
        if (FretboardRef.current.orientation === "landscape") {
            path.lineTo(start.add([end, 0]));
        } else if (FretboardRef.current.orientation === "portrait") {
            path.lineTo(start.add([0, end]));
        }
        return;
    }

    /**
     * Draws a fret on the fretboard.
     *
     * @param {number} num - The number of the fret to be drawn.
     * @returns {void}
     */

    function drawFret(num) {
        var path, start, x;
        path = new paper.Path();
        path.strokeColor = FretboardRef.current.color;
        x = getFretX(num);
        if (FretboardRef.current.orientation === "landscape") {
            start = new paper.Point(x, FretboardRef.current.y);
        } else if (FretboardRef.current.orientation === "portrait") {
            start = new paper.Point(FretboardRef.current.y, x);
        }
        path.moveTo(start);
        if (FretboardRef.current.orientation === "landscape") {
            path.lineTo(start.add([0, (stringCountRef.current * string_spacing - (string_spacing / 2)) - FretboardRef.current.x]))
        } else if (FretboardRef.current.orientation === "portrait") {
            path.lineTo(start.add([(stringCountRef.current * string_spacing - (string_spacing / 2)) - FretboardRef.current.x, 0]));
        }
        return;
    }

    function drawNut() {
        const nutWidth = FretboardRef.current.orientation === "portrait" ? stringCountRef.current * string_spacing : FretboardRef.current.nut_width;
        const nutHeight = FretboardRef.current.orientation === "portrait" ? FretboardRef.current.nut_width : string_spacing * stringCountRef.current;
        const xCoord = FretboardRef.current.orientation === "portrait" ? FretboardRef.current.x : FretboardRef.current.x - nutWidth / 2;
        const yCoord = FretboardRef.current.orientation === "portrait" ? FretboardRef.current.x : FretboardRef.current.y;
        const nutPath = new paper.Path.RoundRectangle(
            xCoord,
            yCoord - 5,
            nutWidth,
            nutHeight
        );
        nutPath.strokeColor = FretboardRef.current.nut_color;
        nutPath.fillColor = FretboardRef.current.nut_color;

        // Draw the tuning letters on the nut

        const tuningLetters = FretboardRef.current.orientation === "landscape" ? tuningRef.current : [...tuningRef.current].reverse();
        if (showTuningOnNutRef.current) {
            for (let i = 0; i < stringCountRef.current; i++) {
                const note = tuningLetters[i];
                const x = FretboardRef.current.orientation === "portrait" ? xCoord + i * string_spacing + (string_spacing / 2) : xCoord + FretboardRef.current.nut_width / 2;
                const y = FretboardRef.current.orientation === "portrait" ? yCoord + FretboardRef.current.nut_width / 2 + svgPaddingX - 5 : yCoord + i * string_spacing + (string_spacing / 2) - svgPaddingX;
                const text = new paper.PointText(new paper.Point(x, y));
                text.content = note;
                text.fillColor = FretboardRef.current.nut_font_color;
                text.fontFamily = FretboardRef.current.font_face;
                text.fontSize = FretboardRef.current.font_size;
                text.justification = "center";
            }
        }

        return nutPath;
    }

    function showStartFret() {
        var center;
        center = getFretCenter(FretboardRef.current.start_fret, 1);
        return renderText(new paper.Point(center.x, 10), FretboardRef.current.start_fret); //FretboardRef.current.y - string_spacing / 2, fretboardTitleRef.current
    }


    /**
     * Displays the description of the fretboard.
     * The text starts at the point defined by the center of the fretboard and the bottom of the fretboard, and grows to the right.
     * 
     * @returns {JSX.Element} The rendered text element.
     */
    function showDescription() {
        //the text should start at the point defined by the center of the fretboard and the bottom of the fretboard but grow to the right
        var center;
        center = getFretCenter(FretboardRef.current.start_fret, stringCountRef.current);
        if (FretboardRef.current.orientation === "landscape") {
            return renderText(new paper.Point(center.x, center.y + 15), fretboardDescriptionRef.current, "black", 14, "left");
        } else if (FretboardRef.current.orientation === "portrait") {
            return renderText(new paper.Point(center.x + 10, center.y), fretboardDescriptionRef.current, "black", 14, "left");
        }

    }

    function getFretX(num) {
        const fretIndex = num - (Fretboard.start_fret - 1);
        const fretX = fretXs[fretIndex];
        const xOffset = Fretboard.start_fret > 1 ? 3 : Fretboard.nut_width;
        const fretXWithOffset = fretX + xOffset;
        return fretXWithOffset;
    }

    function getStringPos(num, pos = FretboardRef.current.y) {
        return pos + ((num - 1) * string_spacing);
    }

    /**
     * Calculates the center point of a fret on the fretboard.
     *
     * @param {number} fret - The fret number.
     * @param {number} string - The string number.
     * @param {boolean} [centerMarker=true] - Whether to include a center marker.
     * @returns {paper.Point} - The center point of the fret.
     */
    function getFretCenter(fret, string, centerMarker = true) {
        var x, y; //end_fret, start_fret,
        x = 0;
        let firstVar = 0;
        let secondVar = 0;
        if (fret === 0) {
            x = FretboardRef.current.orientation === "landscape" ? getFretX(0) : getFretX(0);
        } else {
            firstVar = getFretX(fret);
            secondVar = getFretX(fret - 1);
            x = centerMarker ? (firstVar + secondVar) / 2 : firstVar;
        }
        y = getStringPos(string, FretboardRef.current.y);
        return FretboardRef.current.orientation === "landscape" ? new paper.Point(x, y) : new paper.Point(y, x);
    }

    /**
     * Draws a watermark on the fretboard if the user is not a premium user.
     * The watermark displays the website URL based on the orientation of the fretboard.
     */
    function drawWatermark() {
        if (!isPremium) {
            var text;
            //if the orientation is landscape, or FretboardRef.current.orientation is undefined,  the watermark should be in the top right corner
            if (FretboardRef.current.orientation === undefined || FretboardRef.current.orientation === "landscape") {
                text = new paper.PointText(new paper.Point(paper.view.viewSize.width - 200, 10));
                text.content = "created with www.guitartabcreator.com";
            } else if (FretboardRef.current.orientation === "portrait") {
                text = new paper.PointText(new paper.Point(5, paper.view.viewSize.height - 5));
                text.content = "www.guitartabcreator.com";
            }
            text.fillColor = "black";
            text.fontFamily = "Arial";
            text.fontWeight = "bold";
            text.fontSize = 10;
            return text.justification = "left";
        }
    }

    function drawMarkers() {
        var bottom_dot, drawCircle, i, j, len, len1, middle_dot, position, ref, ref1, results, start, top_dot, y_displacement;
        middle_dot = 3;
        top_dot = 4;
        bottom_dot = 2;
        if (parseInt(stringCountRef.current, 10) === 4) {
            middle_dot = 2;
            top_dot = 3;
            bottom_dot = 1;
        }

        drawCircle = (start) => {
            var path;
            path = new paper.Path.Circle(start, FretboardRef.current.marker_radius);
            path.strokeColor = FretboardRef.current.marker_color;
            return path.fillColor = FretboardRef.current.marker_color;
        };

        y_displacement = string_spacing / 2;
        ref = [3, 5, 7, 9, 15, 17, 19, 21];
        for (i = 0, len = ref.length; i < len; i++) {
            position = ref[i];
            if (hasFret(position)) {
                start = FretboardRef.current.orientation === "landscape" ? getFretCenter(position, middle_dot).add([0, y_displacement]) : getFretCenter(position, middle_dot).add([y_displacement, 0]);
                drawCircle(start);
            }
        }
        ref1 = [12, 24];
        results = [];
        for (j = 0, len1 = ref1.length; j < len1; j++) {
            position = ref1[j];
            if (hasFret(position)) {
                start = FretboardRef.current.orientation === "landscape" ? getFretCenter(position, middle_dot).add([0, y_displacement]) : getFretCenter(position, middle_dot).add([y_displacement, 0]);
                drawCircle(start);
                start = FretboardRef.current.orientation === "landscape" ? getFretCenter(position, bottom_dot).add([0, y_displacement]) : getFretCenter(position, bottom_dot).add([y_displacement, 0]);
                drawCircle(start);
                start = FretboardRef.current.orientation === "landscape" ? getFretCenter(position, top_dot).add([0, y_displacement]) : getFretCenter(position, top_dot).add([y_displacement, 0]);
                results.push(drawCircle(start));
            } else {
                results.push(void 0);
            }
        }
        return results;
    }

    function hasFret(num) {
        return num >= FretboardRef.current.start_fret && num <= FretboardRef.current.end_fret;
    }

    function hasString(num) {
        return num >= 1 && num <= stringCountRef.current;
    }

    function parse() {
        var i, len, line, lines, match;
        lines = divRef.value.split(/\r?\n/);
        for (i = 0, len = lines.length; i < len; i++) {
            line = lines[i];
            line.trim();
            match = line.match(/^\s*option\s+(\S+)\s*=\s*(\S+)/);
            if (match != null) {
                setOption(match[1], match[2]);
            }
            match = line.match(/^\s*show\s+(.+)/);
            if (match != null) {
                show(match[1]);
            }
        }
        return true;
    }

    function show(line) {
        var i, len, match, option, options, params, ref, valid_options;
        options = line.split(/\s+/);
        params = {};
        valid_options = ["fret", "frets", "string", "strings", "text", "color", "note", "notes", "fill-color", "text-color", "color-scheme"];
        for (i = 0, len = options.length; i < len; i++) {
            option = options[i];
            match = option.match(/^(\S+)\s*=\s*(\S+)/);
            if (match === null) {
                setError("Invalid 'show' option: " + option);
            } else if (ref = match[1], valid_options.indexOf(ref) < 0) {
                setError("Invalid 'show' option: " + match[1]);
            }
            if (match != null) {
                params[match[1]] = match[2];
            }
        };
        return lights.push(params);
    }

    function lightsCameraAction() {
        var i, len, light, param, params, ref, results;
        ref = lights;
        results = [];
        for (i = 0, len = ref.length; i < len; i++) {
            light = ref[i];
            params = extractFrets(light);
            results.push((function () {
                var j, len1, results1;
                results1 = [];
                for (j = 0, len1 = params.length; j < len1; j++) {
                    param = params[j];
                    if (light.color != null) {
                        param.color = light.color;
                    }
                    if (light["fill-color"] != null) {
                        param.fillColor = light["fill-color"];
                    }
                    if (light["text-color"] != null) {
                        param.textColor = light["text-color"];
                    }
                    if (light.text != null) {
                        param.text = light.text;
                        results1.push(lightText(param));
                    } else {
                        results1.push(lightUp(param));
                    }
                }
                return results1;
            }).call(this));
        }
        return results;
    }

    function extractNumbers(str) {
        str.trim();
        return str.split(/\s*,\s*/);
    };

    function extractNotes(str) {
        var extracted_notes, i, len, note, notes, parts;
        str.trim();
        notes = str.split(/\s*,\s*/);
        extracted_notes = [];
        for (i = 0, len = notes.length; i < len; i++) {
            note = notes[i];
            parts = note.match(/(\d+)\/(\d+)/);
            if (parts != null) {
                extracted_notes.push({
                    fret: parseInt(parts[1], 10),
                    string: parseInt(parts[2], 10)
                });
            } else {
                setError("Invalid note: " + note);
            }
        }
        return extracted_notes;
    };

    function extractFrets(light) {
        var fret, frets, i, j, l, len, len1, len2, lights, note, notes, string, strings;
        if (light.fret != null) {
            frets = extractNumbers(light.fret);
        }
        if (light.frets != null) {
            frets = extractNumbers(light.frets);
        }
        if (light.string != null) {
            strings = extractNumbers(light.string);
        }
        if (light.strings != null) {
            strings = extractNumbers(light.strings);
        }
        if (light.note != null) {
            notes = extractNotes(light.note);
        }
        if (light.notes != null) {
            notes = extractNotes(light.notes);
        }
        if ((!((frets != null) && (strings != null))) && (notes == null)) {
            setError("No frets or strings specified on line");
        }
        lights = [];
        if ((frets != null) && (strings != null)) {
            for (i = 0, len = frets.length; i < len; i++) {
                fret = frets[i];
                for (j = 0, len1 = strings.length; j < len1; j++) {
                    string = strings[j];
                    lights.push({
                        fret: parseInt(fret, 10),
                        string: parseInt(string, 10)
                    });
                }
            }
        }
        if (notes != null) {
            for (l = 0, len2 = notes.length; l < len2; l++) {
                note = notes[l];
                lights.push(note);
            }
        }
        return lights;
    };

    function lightText(options) {
        var opts, path, point, y_displacement;
        opts = {
            color: "white",
            fillColor: "#666",
            ...options
        };
        point = getFretCenter(opts.fret, opts.string, centerMarkers);
        path = new paper.Path.Circle(point, light_radius);
        path.strokeColor = opts.color;
        path.fillColor = opts.fillColor;
        y_displacement = light_radius / 2;
        //these are displacements of the text to make it centered.
        //the conversion to landscape vs portrait is done in getFretCenter, so these are real relative to axis
        if (FretboardRef.current.orientation === "landscape") {
            point.y += y_displacement;
        } else {
            //point.x -= 5;
            point.y += y_displacement;
        }

        if (opts.text != null) {
            if (opts.textColor !== undefined) {
                renderText(point, opts.text, opts.textColor);
            } else {
                renderText(point, opts.text, opts.color);
            }
        }
        //rotate if portrait

        return paper.view.draw();
    }

    function lightUp(options) {
        var path, point;
        if (options.color == null) {
            options.color = '#666';
        }
        if (options.fillColor == null) {
            options.fillColor = options.color;
        }
        point = getFretCenter(options.fret, options.string, centerMarkers);
        path = new paper.Path.Circle(point, light_radius - 2);
        path.strokeColor = options.color;
        path.fillColor = options.fillColor;
        return paper.view.draw();
    }

    function addChord() {
        for (let i = 0, len = props.cursorNotes.length; i < len; i++) {
            lightUp({
                fillColor: '#0F0',
                fret: props.cursorNotes[i][0],
                string: props.cursorNotes[i][1],
            })
        }
    }

    /**
     * Renders text on the fretboard.
     *
     * @param {paper.Point} point - The position of the text.
     * @param {string} value - The text to be rendered.
     * @param {string} [color=FretboardRef.current.font_color] - The color of the text.
     * @param {number} [size=FretboardRef.current.font_size] - The font size of the text.
     * @param {string} [justification="center"] - The justification of the text.
     * @returns {paper.PointText} - The rendered text object.
     */
    function renderText(point, value, color = FretboardRef.current.font_color, size = FretboardRef.current.font_size, justification = "center") {
        var text;
        text = new paper.PointText(point);
        text.justification = justification;
        text.characterStyle = {
            font: FretboardRef.current.font_face,
            fontSize: size,
            fillColor: color
        };
        if (FretboardRef.current.orientation === "portrait") {
            // text.rotate(90);
        }
        return text.content = value;
    }

    return (
        <Grid container spacing={1} sx={{ flexDirection: "row" }}>
            {isOld && !props.viewer &&
                <OldFretboardModal
                    setIsOld={setIsOld}
                    setShowCode={setShowCode}
                    setShowFretboardModal={setShowFretboardModal}
                    handleSaveButtonClick={handleSaveButtonClick}
                    codeDivRef={divRef}
                />
            }
            <Grid item xs={12}>

                <Box sx={styles.boxStyle}>
                    {!props.viewer &&
                        <Box sx={styles.headerStyle}>
                            <FretboardSettings
                                setFretboardTitle={setTitle}
                                setFretboardStringCount={setStringCount}
                                Fretboard={Fretboard}
                                setFretboard={setFretboard}
                                premium={isPremiumRef.current}
                                setIsPremium={setIsPremium}
                                handleUpgrade={handleUpgrade}
                                showCode={showCode}
                                disabled={disableShowCode}
                                setShowCode={setShowCode}
                                showFretboardModal={showFretboardModal}
                                setShowFretboardModal={setShowFretboardModal}
                                centerMarkers={centerMarkers}
                                setCenterMarkers={setCenterMarkers}
                                isAnon={props.isAnon}
                            />
                            <TuningControl
                                setTuning={setTuning}
                                setShowCode={setShowCode}
                                tuning={tuning}
                                stringCount={stringCount}
                                setShowTuningOnNut={setShowTuningOnNut}
                                showTuningOnNut={showTuningOnNut}
                                premium={isPremium}
                                disabled={disableShowCode}
                            />
                            <Button
                                id="code-editor-button"
                                aria-controls={showCode ? 'basic-menu' : undefined}
                                onClick={() => handleShowCode(!showCode)}
                                variant="outlined"
                                size='small'
                                disabled={disableShowCode}
                                sx={{ marginRight: '5px' }}
                                endIcon={<CodeIcon />}
                            >
                                Editor
                            </Button>
                            <FretboardDiagramTour />
                        </Box>
                    }
                    {props.viewer ? <Typography variant="h4" sx={{ marginTop: '10px', display: 'block' }}>Primary Fretboard</Typography> : ""}
                    <Grid id="fretboard_control_container" container spacing={2} sx={{
                        flexDirection: { xs: 'column', lg: 'row' }
                    }}>
                        <Grid id="fretbard-canvas-item" item sx={{
                            width: (paper.project && paper.project.view.viewSize.width) ? paper.project.view.viewSize.width : 754,
                            height: (paper.project && paper.project.view.viewSize.height) ? paper.project.view.viewSize.height : 144
                        }}>
                            <canvas id="fretboard-canvas" ref={canvasRef} />

                        </Grid>
                        {!showCode && !props.viewer &&
                            <Grid item id="fretboard-meta-data-item">
                                <NoteMetaData
                                    setNoteBorderColor={setNoteBorderColor}
                                    setNoteFillColor={setNoteFillColor}
                                    setNoteTextColor={setNoteTextColor}
                                    setNoteText={setNoteText}
                                    noteNumber={noteNumberRef.current}
                                    setNoteNumber={setNoteNumber}
                                    autoIncrement={autoIncrement}
                                    setAutoIncrement={setAutoIncrement}
                                    showNumbers={showNumbers}
                                    setShowNumbers={setShowNumbers}
                                    setShowFretNote={setShowFretNote}
                                    xcrfToken={xcsrf}
                                    isPremium={isPremium}
                                    setIsPremium={setIsPremium}
                                    orientation={FretboardRef.current.width < 500 ? "portrait" : "landscape"}
                                    isAnon={props.isAnon}
                                    redrawFretboard={redrawFretboard}
                                    viewer={props.viewer}
                                    description={fretboardDescriptionRef.current}
                                    updateDescription={setFretboardDescription}
                                    removeAllSelectedFrets={removeAllSelectedFrets}
                                />
                            </Grid>}
                    </Grid>
                </Box>
                {displayCodeBlock && !props.viewer &&
                    <Box sx={{ ...styles.boxStyle, marginTop: '20px' }}>
                        <CodeInput textareaRef={divRef} showCode={showCode} setUpdateCode={setUpdateCode} handleShowCode={handleShowCode} />
                    </Box>
                }
                <FretboardMenu
                    drawings={Drawings}
                    replaceSelectedFrets={replaceSelectedFrets}
                    handleSaveButtonClick={handleSaveButtonClick}
                    updateDrawings={updateDrawings}
                    width={FretboardRef.current.width}
                    height={FretboardRef.current.height}
                    premium={isPremium}
                    animate={props.isAnon ? true : animateFretboardRef.current} //if the user is not logged in, the fretboard will animate by default
                    handleAnimateToggle={handleAnimateToggle}
                    updateDrawing={updateDrawing}
                    selectedFrets={selectedFrets}
                    cancelReplaceSelectedFrets={cancelReplaceSelectedFrets}
                    noEdit={props.viewer}
                    handleDownloadImage={handleDownloadImage}
                    backgroundColor={FretboardRef.current.background_color}
                />
            </Grid>
            <Paywall open={showPaywall} setIsPremium={setIsPremium} isAnon={props.isAnon} /> {/* Render the Paywall component */}
        </Grid>
    );
}

export default Fretboard;
